import { DialogComponent } from '@theme/dialog';
import { ThemeEvents } from '@theme/events';

const KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
const KLAVIYO_PROFILES = 'https://a.klaviyo.com/client/profiles/';
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/**
 * Back-in-stock signup popup for a single sold-out variant.
 *
 * Opens itself when the shopper picks a strength that is sold out, and posts
 * their email straight to Klaviyo, which fires "Subscribed to Back in Stock"
 * and enqueues them on the Back In Stock flow.
 *
 * @extends DialogComponent
 */
class NotifyMeForm extends DialogComponent {
  requiredRefs = ['dialog'];

  /** @type {Record<string, {available: boolean, label: string}>} */
  #variants = {};

  /** @type {Record<string, string>} */
  #strings = {};

  /** Variants we have already popped open for, so we never nag twice for the same strength. */
  #opened = new Set();

  /** Variants this shopper has already signed up for in this pageview. */
  #submitted = new Set();

  /**
   * Shopify renders the <notify-me-form> wrapper from the block's schema tag, so
   * the block's own data attributes live on this inner element, not on `this`.
   * @type {HTMLElement | null}
   */
  #config = null;

  connectedCallback() {
    super.connectedCallback();

    this.#config = this.querySelector('.notify-me');
    this.#variants = this.#readVariants();
    this.#strings = this.#readStrings();

    const section = this.closest('.shopify-section, dialog') ?? document;

    /** @param {CustomEvent & {detail: {resource: {id: string|number, available: boolean}|null}}} event */
    const variantUpdated = (event) => {
      const variant = event.detail.resource;
      if (!variant) return;
      this.#applyVariant(String(variant.id), variant.available);
    };

    section.addEventListener(ThemeEvents.variantUpdate, variantUpdated);

    this.disconnectedCallback = () => {
      section.removeEventListener(ThemeEvents.variantUpdate, variantUpdated);
      DialogComponent.prototype.disconnectedCallback.call(this);
    };
  }

  /** @returns {Record<string, {available: boolean, label: string}>} */
  #readVariants() {
    const script = this.querySelector('[data-notify-me-variants]');
    if (!script?.textContent) return {};
    try {
      return JSON.parse(script.textContent);
    } catch (_error) {
      return {};
    }
  }

  /** @returns {Record<string, string>} */
  #readStrings() {
    const node = this.querySelector('.notify-me__strings');
    return node ? { ...node.dataset } : {};
  }

  /**
   * Points the popup at a different strength, and opens it the first time that
   * strength turns out to be sold out. Never opens on page load — only a real
   * variant change gets here.
   * @param {string} variantId
   * @param {boolean} available
   */
  #applyVariant(variantId, available) {
    if (this.#config) this.#config.dataset.variantId = variantId;

    const { strength, trigger } = this.refs;
    const label = this.#variants[variantId]?.label;
    if (strength && label) strength.textContent = label;
    if (trigger) trigger.hidden = available;

    if (available) {
      this.closeDialog();
      return;
    }

    this.#resetForm(variantId);

    if (!this.#opened.has(variantId)) {
      this.#opened.add(variantId);
      this.showDialog();
    }
  }

  /**
   * Restores the form, unless this shopper already signed up for this strength.
   * @param {string} variantId
   */
  #resetForm(variantId) {
    const { body, message, submit, email } = this.refs;

    if (this.#submitted.has(variantId)) {
      if (body) body.hidden = true;
      this.#showMessage(this.#strings.success ?? '');
      return;
    }

    if (body) body.hidden = false;
    if (message) {
      message.hidden = true;
      message.textContent = '';
      message.classList.remove('notify-me__message--error');
    }
    if (submit) {
      submit.disabled = false;
      submit.textContent = this.#strings.button ?? submit.textContent;
    }
    if (email) email.value = '';
  }

  /**
   * @param {string} text
   * @param {boolean} [isError]
   */
  #showMessage(text, isError = false) {
    const { message } = this.refs;
    if (!message) return;
    message.textContent = text;
    message.classList.toggle('notify-me__message--error', isError);
    message.hidden = false;
  }

  /** @param {SubmitEvent} event */
  handleSubmit = (event) => {
    event.preventDefault();

    const { email, submit, body } = this.refs;
    const variantId = this.#config?.dataset.variantId;
    const companyId = this.#config?.dataset.companyId;
    const prefix = this.#config?.dataset.catalogPrefix ?? '';
    const revision = this.#config?.dataset.revision ?? '2026-07-15';

    if (!email || !variantId || !companyId) return;

    const address = email.value.trim();
    if (!EMAIL_PATTERN.test(address)) {
      this.#showMessage(this.#strings.invalid ?? '', true);
      email.focus();
      return;
    }

    if (submit) {
      submit.disabled = true;
      submit.textContent = this.#strings.sending ?? submit.textContent;
    }

    const payload = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          channels: ['EMAIL'],
          profile: {
            data: {
              type: 'profile',
              attributes: { email: address },
            },
          },
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: `${prefix}${variantId}`,
            },
          },
        },
      },
    };

    const headers = { 'Content-Type': 'application/vnd.api+json', revision };
    const language = this.#config?.dataset.language ?? '';
    const country = this.#config?.dataset.country ?? '';

    // The back-in-stock endpoint rejects profile properties, so the storefront language goes
    // on the profile first. The flow routes the restock email on bis_language. A failure here
    // must not block the signup itself: the flow falls back to English.
    const profile = {
      data: { type: 'profile', attributes: { email: address, properties: { bis_language: language, bis_country: country } } },
    };
    fetch(`${KLAVIYO_PROFILES}?company_id=${encodeURIComponent(companyId)}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(profile),
    })
      .catch(() => {})
      .then(() => fetch(`${KLAVIYO_ENDPOINT}?company_id=${encodeURIComponent(companyId)}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    }))
      .then((response) => {
        if (!response.ok) throw new Error(`Klaviyo responded ${response.status}`);
        this.#submitted.add(variantId);
        if (body) body.hidden = true;
        this.#showMessage(this.#strings.success ?? '');
      })
      .catch(() => {
        if (submit) {
          submit.disabled = false;
          submit.textContent = this.#strings.button ?? submit.textContent;
        }
        this.#showMessage(this.#strings.error ?? '', true);
      });
  };
}

if (!customElements.get('notify-me-form')) {
  customElements.define('notify-me-form', NotifyMeForm);
}
